import { useCallback, useRef, useState } from 'react';
import { DRAG_STATE, STAGE_EVENTS } from '../constants';
import type { DragEventT, InterceptorStoreT, StageEventDetailT, StageEventType, StagePointerEventT, StageSettingsT } from '../types';
import { getMousePos } from '../utils/get-mouse-pos';
type StagePointerEventsParamsT = {
  stageRef: React.RefObject<HTMLDivElement>;
  stageBoundingRectRef: React.MutableRefObject<ClientRect | null | undefined>;
  stageEventTarget: EventTarget;
  interceptorStoreRef: React.RefObject<InterceptorStoreT>;
  getStageSettings: () => StageSettingsT;
  isDraggable: boolean;
  readOnly: boolean;
};
type PointerHandlersT = {
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onPointerEnter?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerLeave?: (e: React.PointerEvent<HTMLDivElement>) => void;
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
};
export function useStagePointerEvents({
  stageRef,
  stageBoundingRectRef,
  stageEventTarget,
  interceptorStoreRef,
  getStageSettings,
  isDraggable,
  readOnly
}: StagePointerEventsParamsT) {
  const [onStage, setOnStage] = useState(true);
  const dragEventRef = useRef<DragEventT | null>(null);
  const isPointerDownRef = useRef(false);
  const lastMousePositionRef = useRef<StagePointerEventT | undefined>(undefined);
  const isDraggableRef = useRef(isDraggable);
  // eslint-disable-next-line react-hooks/refs
  isDraggableRef.current = isDraggable;
  const runInterceptors = useCallback((event: StageEventType, stageEvent: StagePointerEventT): StagePointerEventT => (interceptorStoreRef.current?.[event] ?? []).reduce((evt, fn) => fn(evt), stageEvent), [interceptorStoreRef]);
  const buildStageEvent = useCallback((e: React.PointerEvent<HTMLDivElement>, eventType: StageEventType): StagePointerEventT => {
    const settings = getStageSettings();
    const mousePos = getMousePos(e, stageBoundingRectRef.current, settings.scale, settings.rotation);
    const drag = dragEventRef.current;
    const detail: StageEventDetailT = {
      mousePos,
      scale: settings.scale,
      shiftKey: e.shiftKey,
      mouseLeftDown: e.buttons === 1,
      deltaX: drag ? mousePos[0] - drag.dragStartMousePos[0] : 0,
      deltaY: drag ? mousePos[1] - drag.dragStartMousePos[1] : 0,
      dragTarget: drag ? (drag.target as Element) ?? undefined : undefined
    };
    return runInterceptors(eventType, (({
      ...e,
      detail
    } as unknown) as StagePointerEventT));
  }, [getStageSettings, stageBoundingRectRef, runInterceptors]);
  const dispatch = useCallback((eventType: StageEventType, stageEvent: StagePointerEventT) => {
    stageEventTarget.dispatchEvent(new CustomEvent(eventType, stageEvent));
  }, [stageEventTarget]);
  const isSuppressed = (e: React.PointerEvent<HTMLDivElement>) => e.altKey;
  const isClickedOnStage = (e: React.PointerEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement);
    return target.tagName === 'svg' && Boolean(target.getAttribute('data-stage-svg'));
  };

  // data-stage-draggable: element captures drag (e.g. annotation vertex)
  // data-stage-custom: element handles pointer-down itself but wants drag tracking
  const isDraggableTarget = (e: React.PointerEvent<HTMLDivElement>) => (e.target as HTMLElement).getAttribute('data-stage-draggable') !== null;
  const isCustomTarget = (e: React.PointerEvent<HTMLDivElement>) => (e.target as HTMLElement).getAttribute('data-stage-custom') !== null;
  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const settings = getStageSettings();
    const mousePos = getMousePos(e, stageBoundingRectRef.current, settings.scale, settings.rotation);
    dragEventRef.current = {
      dragState: DRAG_STATE.DRAG_START,
      dragStartMousePos: mousePos,
      target: e.target
    };
  };
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isSuppressed(e) || e.button !== 0 || dragEventRef.current !== null) return;
    if (isCustomTarget(e)) {
      startDrag(e);
      return;
    }
    if (isDraggableRef.current || isDraggableTarget(e)) {
      if (!isClickedOnStage(e)) {
        startDrag(e);
        return;
      }
    }
    if (isPointerDownRef.current) return;
    isPointerDownRef.current = true;
    dispatch(STAGE_EVENTS.POINTER_DOWN, buildStageEvent(e, STAGE_EVENTS.POINTER_DOWN));
  }, [buildStageEvent, dispatch]);
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isSuppressed(e)) return;
    const drag = dragEventRef.current;
    if (drag) {
      if (drag.dragState === DRAG_STATE.DRAG_START) {
        dispatch(STAGE_EVENTS.DRAG_START, buildStageEvent(e, STAGE_EVENTS.DRAG_START));
      }
      dragEventRef.current!.dragState = DRAG_STATE.DRAG;
      dispatch(STAGE_EVENTS.DRAG, buildStageEvent(e, STAGE_EVENTS.DRAG));
      return;
    }
    const moveEvent = buildStageEvent(e, STAGE_EVENTS.POINTER_MOVE);
    lastMousePositionRef.current = moveEvent;
    dispatch(STAGE_EVENTS.POINTER_MOVE, moveEvent);
  }, [buildStageEvent, dispatch]);
  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isSuppressed(e)) return;
    const drag = dragEventRef.current;
    if (drag) {
      if (drag.dragState === DRAG_STATE.DRAG) {
        dispatch(STAGE_EVENTS.DRAG_END, buildStageEvent(e, STAGE_EVENTS.DRAG_END));
      }
      dragEventRef.current = null;
      return;
    }
    dispatch(STAGE_EVENTS.POINTER_UP, buildStageEvent(e, STAGE_EVENTS.POINTER_UP));
    isPointerDownRef.current = false;
  }, [buildStageEvent, dispatch]);
  const onPointerEnter = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isSuppressed(e)) return;
    dispatch(STAGE_EVENTS.POINTER_ENTER, buildStageEvent(e, STAGE_EVENTS.POINTER_ENTER));
    setOnStage(true);
  }, [buildStageEvent, dispatch]);
  const onPointerLeave = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isSuppressed(e)) return;
    dispatch(STAGE_EVENTS.POINTER_LEAVE, buildStageEvent(e, STAGE_EVENTS.POINTER_LEAVE));
    setOnStage(false);
  }, [buildStageEvent, dispatch]);
  const onContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(STAGE_EVENTS.RIGHT_CLICK, buildStageEvent(((e as unknown) as React.PointerEvent<HTMLDivElement>), STAGE_EVENTS.RIGHT_CLICK));
  }, [buildStageEvent, dispatch]);
  const onClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.detail === 2) {
      dispatch(STAGE_EVENTS.DOUBLE_CLICK, buildStageEvent(((e as unknown) as React.PointerEvent<HTMLDivElement>), STAGE_EVENTS.DOUBLE_CLICK));
    }
  }, [buildStageEvent, dispatch]);
  const pointerHandlers: PointerHandlersT = {
    onPointerDown,
    onPointerUp,
    onClick,
    ...(!readOnly && {
      onPointerEnter,
      onPointerMove,
      onPointerLeave,
      onContextMenu
    })
  };
  return {
    onStage,
    lastMousePositionRef,
    pointerHandlers
  };
}